import { useFormik } from 'formik';
import React, { useEffect, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import * as Yup from 'yup';
import '../assets/css/allpages.css';
import '../assets/css/form-validation.css';
import CustomInput from '../components/common/CustomInput';
import PhoneInputGroup from '../components/common/PhoneInputGroup';
import StaticPageHeader from '../components/common/StaticPageHeader';
import TopAddress from '../components/TopAddress';
import { callGetWebservice, callPostWebservice } from '../hooks/api';
import { getNationalPhoneNumber, getParsedPhone, validatePhone } from '../utils/Utilities';



const AddAccountAddress = () => {
    const location = useLocation();
    const { address } = location.state || {};

    const [countries, setCountries] = useState([]);
    const [states, setStates] = useState([]);
    const [cities, setCities] = useState([]);
    const [areas, setAreas] = useState([]);

    const [showState, setShowState] = useState(false);
    const [showCity, setShowCity] = useState(false);
    const [showArea, setShowArea] = useState(false);

    const navigate = useNavigate();

    useEffect(() => {
        callGetWebservice('countries', {}, (response) => {
            setCountries(response.data);
        });
    }, []);

    const validationSchema = React.useMemo(() => Yup.object({
        fullName: Yup.string().required("Full Name is required"),
        mobileNumber: Yup.string()
            .required("Mobile Number is required")
            .test('is-valid-phone', 'Use a national or international number valid for the selected country.', function (value) {
                const { country: countryId } = this.parent;
                if (!countryId || !value) return true;
                const country = countries.find(c => c.id === countryId);
                if (country) {
                    return validatePhone(value, country.iso2);
                }
                return true;
            }),
        country: Yup.string().required("Country is required"),
        state: Yup.string().required("State is required"),
        city: Yup.string().required("City is required"),
        area: Yup.string().required("Area is required"),
        zipCode: Yup.string().min(5, "ZIP/Postcode must be at least 5 characters").required("ZIP/Postcode is required"),
        address: Yup.string().required("Address is required"),
        type: Yup.string().required('Type is required'),
    }), [countries]);

    const formik = useFormik({
        initialValues: {
            fullName: address?.name || "", mobileNumber: address?.phone_number || "",
            country: address?.country_id || "", state: address?.state_id || "",
            city: address?.city_id || "", area: address?.area_id || "",
            zipCode: address?.zip_code || "", address: address?.address || "",
            type: address?.type || "", isDefault: address?.is_default_address || false,
        },
        validationSchema,
        onSubmit: (values, { setSubmitting }) => {
            const country = countries.find(c => c.id === values.country);
            const parsedPhone = country ? getParsedPhone(values.mobileNumber, country.iso2) : null;
            const formattedPhone = parsedPhone ? parsedPhone.number : values.mobileNumber;

            let postData = {
                name: values.fullName, phone_number: formattedPhone, country_id: values.country + "",
                state_id: values.state, city_id: values.city, area_id: values.area,
                zip_code: values.zipCode, type: values.type, is_default_address: values.isDefault,
                address: values.address
            }
            let url = 'addresses';
            if (address) { url = 'update-address'; postData['id'] = address.id; }
            callPostWebservice(url, postData, (response) => {
                alert(response.message);
                setSubmitting(false);
                navigate('/myaddress');
            }, (error) => {
                setSubmitting(false);
                formik.setErrors({ fullName: error.errMessage });
            });
        },
    });

    const handleCountryChange = (input) => {
        const countryId = input.value;
        formik.setFieldValue("country", countryId);
        callGetWebservice('states', { country_id: countryId }, (response) => {
            setStates(response.data); setShowState(true); setCities([]); setAreas([]); setShowCity(false); setShowArea(false);
        });
    };

    const handleStateChange = (input) => {
        const stateId = typeof input === "object" ? input.target.value : input;
        formik.setFieldValue("state", stateId);
        callGetWebservice('cities', { state_id: stateId }, (response) => {
            setCities(response.data); setShowCity(true); setAreas([]); setShowArea(false);
        });
    };

    const handleCityChange = (input) => {
        const cityId = typeof input === "object" ? input.target.value : input;
        formik.setFieldValue("city", cityId);
        callGetWebservice('areas', { city_id: cityId }, (response) => {
            setShowArea(true); setAreas(response.data);
        });
    };

    useEffect(() => {
        if (address && countries.length > 0) {
            if (address.country_id) {
                const country = countries.find(c => c.id === address.country_id);
                if (country && address.phone_number) {
                    const nationalPhone = getNationalPhoneNumber(address.phone_number, country.phone_code, country.iso2);
                    if (formik.values.mobileNumber !== nationalPhone) {
                        formik.setFieldValue("mobileNumber", nationalPhone);
                    }
                }
                handleCountryChange({ value: address.country_id });
            }
            if (address.state_id) handleStateChange(address.state_id);
            if (address.city_id) handleCityChange(address.city_id);
            if (address.area_id) formik.setFieldValue("area", address.area_id);
        }
    }, [countries]);

    const countryOptions = countries.map(country => ({ value: country.id, label: country.name, iso2: country.iso2, phone_code: country.phone_code }));

    return (
        <div className="static-page-wrapper bg-light min-vh-100 pb-5">
            <StaticPageHeader title={address ? 'Edit Address' : 'Add New Address'} />

            <div className="container static-page-container mt-4">
                <div className="row align-items-start mt-1">
                    <TopAddress />
                    <div className="col-12 col-md-8">
                        <div className="bg-white border shadow-sm p-4 p-md-5 mb-4" style={{ borderRadius: '12px' }}>
                            <h3 className="fw-bold text-dark mb-4" style={{ fontSize: '1.75rem' }}>{address ? 'Edit Address' : 'Add New Address'}</h3>
                            <form onSubmit={formik.handleSubmit}>
                                <div className="row">
                                    <div className="col-md-6 col-12">
                                        <div className="custom-input-container">
                                            <label className="custom-input-label text-dark fw-medium">Address Type *</label>
                                            <select className={`form-control py-2 ${formik.touched.type && formik.errors.type ? "is-invalid" : ""}`} {...formik.getFieldProps("type")}>
                                                <option value="">Select Type</option>
                                                <option value="Billing">Billing Address</option>
                                                <option value="Shipping">Shipping Address</option>
                                            </select>
                                            {formik.touched.type && formik.errors.type ? <div className="text-danger small mt-1">{formik.errors.type}</div> : null}
                                        </div>
                                    </div>
                                    <div className="col-md-6 col-12">
                                        <CustomInput label="Full Name *" name="fullName" placeholder="Enter Full Name" formik={formik} />
                                    </div>
                                    <div className="col-md-6 col-12">
                                        <div className="custom-input-container">
                                            <PhoneInputGroup
                                                countries={countries}
                                                formik={formik}
                                                countryName="country"
                                                phoneName="mobileNumber"
                                                onCountryChange={handleCountryChange}
                                            />
                                        </div>
                                    </div>

                                    {showState && (
                                        <div className="col-md-6 col-12">
                                            <div className="custom-input-container">
                                                <label className="custom-input-label text-dark fw-medium">State *</label>
                                                <select className={`form-control py-2 ${formik.touched.state && formik.errors.state ? "is-invalid" : ""}`} {...formik.getFieldProps("state")} onChange={handleStateChange}>
                                                    <option value="">Select State</option>
                                                    {states.map(state => (<option key={state.id} value={state.id}>{state.name}</option>))}
                                                </select>
                                                {formik.touched.state && formik.errors.state ? <div className="text-danger small mt-1">{formik.errors.state}</div> : null}
                                            </div>
                                        </div>
                                    )}

                                    {showCity && (
                                        <div className="col-md-6 col-12">
                                            <div className="custom-input-container">
                                                <label className="custom-input-label text-dark fw-medium">Town / City *</label>
                                                <select className={`form-control py-2 ${formik.touched.city && formik.errors.city ? "is-invalid" : ""}`} {...formik.getFieldProps("city")} onChange={handleCityChange}>
                                                    <option value="">Select City</option>
                                                    {cities.map(city => (<option key={city.id} value={city.id}>{city.name}</option>))}
                                                </select>
                                                {formik.touched.city && formik.errors.city ? <div className="text-danger small mt-1">{formik.errors.city}</div> : null}
                                            </div>
                                        </div>
                                    )}

                                    {showArea && (
                                        <div className="col-md-6 col-12">
                                            <div className="custom-input-container">
                                                <label className="custom-input-label text-dark fw-medium">Area *</label>
                                                <select className={`form-control py-2 ${formik.touched.area && formik.errors.area ? "is-invalid" : ""}`} {...formik.getFieldProps("area")}>
                                                    <option value="">Select Area</option>
                                                    {areas.map(area => (<option key={area.id} value={area.id}>{area.name}</option>))}
                                                </select>
                                                {formik.touched.area && formik.errors.area ? <div className="text-danger small mt-1">{formik.errors.area}</div> : null}
                                            </div>
                                        </div>
                                    )}

                                    <div className="col-md-6 col-12">
                                        <CustomInput label="ZIP/Postcode *" name="zipCode" placeholder="Enter ZIP/Postcode" formik={formik} />
                                    </div>
                                    <div className="col-12">
                                        <CustomInput label="Address *" name="address" placeholder="Enter Full Address" formik={formik} />
                                    </div>
                                    <div className="col-12 mb-4">
                                        <div className="form-check custom-checkbox mt-2">
                                            <input type="checkbox" className="form-check-input" id="isDefault" {...formik.getFieldProps('isDefault')} />
                                            <label className="form-check-label text-secondary" htmlFor="isDefault">Make this my default address</label>
                                        </div>
                                    </div>
                                </div>
                                <button type="submit" className="btn btn-dark w-100 py-3 fw-medium shadow-sm transition-hover" style={{ borderRadius: '8px' }} disabled={formik.isSubmitting || !formik.isValid}>
                                    {address ? 'Update Address' : 'Save Address'}
                                </button>
                            </form>
                        </div>
                    </div>
                </div>
            </div>
        </div>
    );
}

export default AddAccountAddress;